import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _dec3, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { View } from '@instructure/ui-view';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { passthroughProps, deprecated } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
var styles = {
  componentId: 'bpunF',
  template: function template(theme) {
    return "\n\n.bpunF_bGBk{background-clip:content-box;background-color:".concat(theme.background || 'inherit', ";background-position:50%;background-repeat:no-repeat;background-size:cover;border-color:").concat(theme.borderColor || 'inherit', ";border-style:solid;box-sizing:border-box;height:2.5em;line-height:0;overflow:hidden;text-align:center}\n\n[dir=ltr] .bpunF_bGBk,[dir=rtl] .bpunF_bGBk{text-align:center}\n\n.bpunF_bGBk.bpunF_uUeq{font-size:inherit}\n\n.bpunF_bGBk.bpunF_uUeq,.bpunF_bGBk.bpunF_dfBC{border-width:").concat(theme.borderWidthSmall || 'inherit', "}\n\n.bpunF_bGBk.bpunF_dfBC{font-size:0.75rem}\n\n.bpunF_bGBk.bpunF_doqw{border-width:").concat(theme.borderWidthSmall || 'inherit', ";font-size:1rem}\n\n.bpunF_bGBk.bpunF_ycrn{font-size:1.25rem}\n\n.bpunF_bGBk.bpunF_cMDj,.bpunF_bGBk.bpunF_ycrn{border-width:").concat(theme.borderWidthMedium || 'inherit', "}\n\n.bpunF_bGBk.bpunF_cMDj{font-size:1.5rem}\n\n.bpunF_bGBk.bpunF_eoMd{border-width:").concat(theme.borderWidthMedium || 'inherit', ";font-size:1.75rem}\n\n.bpunF_eYKj{color:").concat(theme.color || 'inherit', ";font-family:").concat(theme.fontFamily || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";letter-spacing:0.0313em;line-height:2.375em}\n\n.bpunF_MrJH{display:none}\n\n.bpunF_cJVF{border-radius:100%;overflow:hidden;position:relative;width:2.5em}\n\n.bpunF_bRWf{width:3em}");
  },
  'root': 'bpunF_bGBk',
  'auto': 'bpunF_uUeq',
  'x-small': 'bpunF_dfBC',
  'small': 'bpunF_doqw',
  'medium': 'bpunF_ycrn',
  'large': 'bpunF_cMDj',
  'x-large': 'bpunF_eoMd',
  'initials': 'bpunF_eYKj',
  'loadImage': 'bpunF_MrJH',
  'circle': 'bpunF_cJVF',
  'rectangle': 'bpunF_bRWf'
};
import theme from "./theme.js";
/**
---
category: components
---
**/

var Avatar = (_dec = testable(), _dec2 = deprecated('8.0.0', {
  inline: 'display',
  variant: 'shape'
}), _dec3 = themeable(theme, styles), _dec(_class = _dec2(_class = _dec3(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(Avatar, _Component);

  var _super = _createSuper(Avatar);

  function Avatar() {
    var _this;

    _classCallCheck(this, Avatar);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));
    _this.state = {
      loaded: false
    };

    _this.handleImageLoaded = function (event) {
      _this.setState({
        loaded: true
      });

      _this.props.onImageLoaded(event);
    };

    return _this;
  }

  _createClass(Avatar, [{
    key: "makeInitialsFromName",
    value: function makeInitialsFromName() {
      var name = this.props.name;

      if (!name || typeof name !== 'string') {
        return;
      }

      name = name.trim();

      if (name.length === 0) {
        return;
      }

      if (name.match(/\s+/)) {
        var names = name.split(/\s+/);
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      } else {
        return name[0].toUpperCase();
      }
    }
  }, {
    key: "renderLoadImage",
    value: function renderLoadImage() {
      // This image element is visually hidden and is here for loading purposes only
      return /*#__PURE__*/React.createElement("img", {
        src: this.props.src,
        className: styles.loadImage,
        alt: this.props.alt,
        onLoad: this.handleImageLoaded,
        "aria-hidden": "true"
      });
    }
  }, {
    key: "renderInitials",
    value: function renderInitials() {
      return /*#__PURE__*/React.createElement("span", {
        className: styles.initials,
        "aria-hidden": "true"
      }, this.makeInitialsFromName());
    }
  }, {
    key: "render",
    value: function render() {
      var _classnames;

      var _this$props = this.props,
          onImageLoaded = _this$props.onImageLoaded,
          props = _objectWithoutProperties(_this$props, ["onImageLoaded"]);

      return /*#__PURE__*/React.createElement(View, Object.assign({}, passthroughProps(props), {
        style: {
          backgroundImage: this.state.loaded ? "url('".concat(this.props.src, "')") : void 0
        },
        className: classnames((_classnames = {}, _defineProperty(_classnames, styles.root, true), _defineProperty(_classnames, styles[this.props.size], true), _defineProperty(_classnames, styles[this.props.variant || this.props.shape], true), _classnames)),
        "aria-label": this.props.alt ? this.props.alt : null,
        role: this.props.alt ? 'img' : null,
        as: this.props.as,
        elementRef: this.props.elementRef,
        margin: this.props.margin,
        display: this.props.display === 'block' || this.props.inline === false ? 'block' : 'inline-block'
      }), this.renderLoadImage(), !this.state.loaded && this.renderInitials());
    }
  }]);

  Avatar.displayName = "Avatar";
  return Avatar;
}(Component), _class2.propTypes = {
  name: PropTypes.string.isRequired,

  /*
   * URL of the image to display as the background image
   */
  src: PropTypes.string,

  /*
   * Accessible label
   */
  alt: PropTypes.string,
  size: PropTypes.oneOf(['auto', 'x-small', 'small', 'medium', 'large', 'x-large']),
  shape: PropTypes.oneOf(['circle', 'rectangle']),

  /**
   * Valid values are `0`, `none`, `auto`, `xxx-small`, `xx-small`, `x-small`,
   * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
   * familiar CSS-like shorthand. For example: `margin="small auto large"`.
   */
  margin: ThemeablePropTypes.spacing,
  display: PropTypes.oneOf(['inline-block', 'block']),

  /**
   * Callback fired when the avatar image has loaded
   */
  onImageLoaded: PropTypes.func,

  /**
   * the element type to render as
   */
  as: PropTypes.elementType,
  // eslint-disable-line react/require-default-props

  /**
   * provides a reference to the underlying html element
   */
  elementRef: PropTypes.func,

  /* eslint-disable react/require-default-props */

  /**
   * __Deprecated - use `display`__
   */
  inline: PropTypes.bool,

  /**
   * __Deprecated - use `shape`__
   */
  variant: PropTypes.oneOf(['circle', 'rectangle'])
  /* eslint-enable react/require-default-props */

}, _class2.defaultProps = {
  src: void 0,
  alt: void 0,
  margin: void 0,
  elementRef: void 0,
  size: 'medium',
  shape: 'circle',
  display: 'inline-block',
  onImageLoaded: function onImageLoaded(event) {}
}, _temp)) || _class) || _class) || _class);
export default Avatar;
export { Avatar };